import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { EmployeePermissions, Location } from '../../types/employee.types';
import { MapPin } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface LocationPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  permissions: EmployeePermissions;
  locations: Location[];
  onLocationPermissionChange: (locationId: string, checked: boolean) => void;
  onSave: () => void;
  employeeId?: number;
}

export const LocationPermissionsModal: React.FC<LocationPermissionsModalProps> = ({
  isOpen,
  onClose,
  permissions,
  locations,
  onLocationPermissionChange,
  onSave,
  employeeId
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!employeeId) {
      onSave();
      onClose();
      return;
    }

    try {
      setSaving(true);
      const locationIds = Array.isArray(permissions.locations) ? permissions.locations : [];

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/employees/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ permissions: { locations: locationIds } })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error saving location access:', errorData);
      } else {
        console.log('✅ Location access saved:', locationIds);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving location access:', error);
    } finally {
      setSaving(false);
    }
  };

  // Ensure permissions.locations is always an array
  const locationPermissions = Array.isArray(permissions.locations) ? permissions.locations : [];
  const selectedCount = locationPermissions.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Acceso a Sucursales</DialogTitle>
          <DialogDescription>
            Selecciona las sucursales a las que el empleado tendrá acceso
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-blue-700">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">
                {selectedCount === 0 ? 'Ninguna sucursal seleccionada' :
                 selectedCount === 1 ? '1 sucursal seleccionada' :
                 `${selectedCount} sucursales seleccionadas`}
              </span>
            </div>
          </div>

          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {locations.map((location) => (
              <div key={location.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id={`location-${location.id}`}
                  checked={locationPermissions.includes(String(location.id))}
                  onCheckedChange={(checked) => onLocationPermissionChange(String(location.id), checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor={`location-${location.id}`} className="text-sm font-medium cursor-pointer">
                    {location.name}
                  </Label>
                  {location.address && (
                    <p className="text-xs text-gray-500 mt-1">{location.address}</p>
                  )}
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      location.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {location.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-macartur-primary rounded-md hover:bg-macartur-accent1 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar Accesos'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
